import React, { useState, useMemo } from 'react';
import { MdEdit, MdAutoFixHigh } from 'react-icons/md';
import { Field, Input } from './Form';
import './nameField.css';

/**
 * NameField — how a person's name is entered, everywhere.
 *
 * The system stores ONE name (`students.name`, `teachers.name`,
 * `users.name`) and never the parts. That is a deliberate decision:
 *
 *   - The old Firebase app composed names inline in 77 places across 25
 *     files, and inconsistently — the attendance list rendered
 *     `firstName + lastName`, silently dropping the father's name,
 *     while the importer joined all three. The same pupil showed two
 *     different names on two screens.
 *   - Arabic names do not split cleanly in three. Real rows in this
 *     school include "أحمد عبد القادر خلو" and "يوسف ياسين حج محمد",
 *     where the family name is itself two words. Any code that splits
 *     on spaces gets these wrong.
 *
 * So: three boxes HELP you type, and what they produce is a single
 * editable full name. The parts are never sent and never stored — once
 * the full name is right, it is the only truth.
 */
export default function NameField({
  value,
  onChange,
  label = 'الاسم الكامل',
  required = false,
  error,
  hint,
  placeholder = 'الاسم الثلاثي',
  guide,
}) {
  const [assist, setAssist] = useState(false);
  const [parts, setParts] = useState({ first: '', father: '', last: '' });

  // Compose left-to-right in Arabic name order, skipping blanks so a
  // half-filled helper never produces double spaces.
  const composed = useMemo(
    () => [parts.first, parts.father, parts.last].map((p) => p.trim()).filter(Boolean).join(' '),
    [parts]
  );

  const setPart = (key) => (e) => {
    const next = { ...parts, [key]: e.target.value };
    setParts(next);
    const built = [next.first, next.father, next.last].map((p) => p.trim()).filter(Boolean).join(' ');
    onChange(built);
  };

  const openAssist = () => {
    // Pre-fill the helper from whatever is already typed, so opening it
    // on an existing name doesn't wipe it. Three words is the common
    // case; anything longer stays whole in the last box, because
    // guessing where a compound family name starts would be wrong.
    const words = (value || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) setParts({ first: '', father: '', last: '' });
    else if (words.length === 1) setParts({ first: words[0], father: '', last: '' });
    else if (words.length === 2) setParts({ first: words[0], father: '', last: words[1] });
    else setParts({ first: words[0], father: words[1], last: words.slice(2).join(' ') });
    setAssist(true);
  };

  return (
    <Field
      label={label}
      required={required}
      error={error}
      hint={hint}
      span
      guide={guide}
    >
      <div className="ui-name-row">
        <Input
          value={value}
          // Read-only while the 3-box helper is open: with both boxes
          // live at once, typing in either one fought the other for
          // the same value — this field mirrors the composed result
          // instead. Free typing only when the helper is closed.
          onChange={assist ? undefined : (e) => onChange(e.target.value)}
          readOnly={assist}
          placeholder={placeholder}
          error={!!error}
        />
        <button
          type="button"
          className={`ui-name-assist-btn${assist ? ' is-active' : ''}`}
          onClick={() => (assist ? setAssist(false) : openAssist())}
          title={assist ? 'إغلاق مساعد الإدخال' : 'إدخال الاسم على ثلاث خانات'}
        >
          {assist ? <MdEdit size={16} /> : <MdAutoFixHigh size={16} />}
          {assist ? 'كتابة حرة' : 'مساعد'}
        </button>
      </div>

      {assist && (
        <div className="ui-name-assist">
          <div className="ui-name-parts">
            <label>
              <span>الاسم</span>
              <input className="ui-input" value={parts.first} onChange={setPart('first')} placeholder="محمود" />
            </label>
            <label>
              <span>اسم الأب</span>
              <input className="ui-input" value={parts.father} onChange={setPart('father')} placeholder="حسين" />
            </label>
            <label>
              <span>الكنية</span>
              <input className="ui-input" value={parts.last} onChange={setPart('last')} placeholder="محمد" />
            </label>
          </div>
          <p className="ui-name-preview">
            الاسم المحفوظ: <strong>{composed || '—'}</strong>
            <span> · اضغط «كتابة حرة» لتعديله يدوياً بعد التركيب</span>
          </p>
        </div>
      )}
    </Field>
  );
}
